import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, MoreHorizontal, Pencil, Plus, SlidersHorizontal, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import TasksTable from '../../components/TasksTable';
import ConfirmModal from '../../components/ConfirmModal';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { spotlightApi } from '../../services/api';
import { spotlightDefaultNames, useProjectsQuery, useSpotlightQuery } from '../../hooks/queries';
import { useMobileTitle } from '../../components/mobile/mobileTitle';
import SpotlightRules from '../../components/spotlight/SpotlightRules';
import { spotlightFailure } from '../../components/spotlight/SpotlightMenu';

/**
 * Spotlight (feature request e154241c, after Upbase): the person's own short list of the tasks
 * that matter now, from every project and team, in sections they arrange. A task gets in from
 * its detail, by a rule the person set, or made here; dragging orders a section or moves a task
 * to another one. Nobody else sees it.
 */
const SpotlightPage = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const names = useMemo(() => spotlightDefaultNames(t), [t]);
  const { data, isLoading } = useSpotlightQuery(names);
  const { data: projects = [] } = useProjectsQuery();
  const [renaming, setRenaming] = useState(null);  // { id, name }
  const [removing, setRemoving] = useState(null);  // section
  const [rulesOpen, setRulesOpen] = useState(false);
  // Rename from a section's menu: the field takes the focus (and a phone its keyboard), not the
  // menu's button, which a closing menu gives it back to.
  const renameInput = useRef(null);
  const renamingId = renaming?.id;
  useEffect(() => {
    if (!renamingId) return undefined;
    const timer = setTimeout(() => renameInput.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [renamingId]);
  useMobileTitle(t('spotlight.title'));

  // Every Spotlight write refreshes it by itself (services/api.js); a failed one undoes what the
  // screen already showed.
  const refresh = () => qc.invalidateQueries({ queryKey: ['spotlight'] });
  const fail = (err) => {
    toast.error(spotlightFailure(t, err));
    refresh();
  };
  const sections = data?.sections || [];
  const bySection = useMemo(() => {
    const out = {};
    (data?.items || []).forEach((item) => {
      // The section's order, not the task's place in its project.
      (out[item.section_id] = out[item.section_id] || []).push({ ...item.task, order: item.order });
    });
    return out;
  }, [data]);
  const count = data?.count ?? 0;  // the tasks still to do: what the limit counts

  // A dragged task (also one from another section) takes its place; the view shows it at once.
  const reorder = (sectionId, ids) => {
    qc.setQueryData(['spotlight', 'view'], (old) => (old ? {
      ...old,
      items: old.items.map((it) => (ids.includes(it.task_id)
        ? { ...it, section_id: sectionId, order: ids.indexOf(it.task_id) } : it)),
    } : old));
    spotlightApi.reorderItems(sectionId, ids).catch(fail);
  };
  // A change made in a row (completed, a date, a title) shows at once; the write refreshes it.
  const patchTask = (taskId, patch) => qc.setQueryData(['spotlight', 'view'], (old) => (old ? {
    ...old,
    items: old.items.map((it) => (it.task_id === taskId ? { ...it, task: { ...it.task, ...patch } } : it)),
  } : old));
  const moveSection = (index, by) => {
    const ids = sections.map((s) => s.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + by, 0, moved);
    spotlightApi.reorderSections(ids).catch(fail);
  };
  const addSection = () => {
    spotlightApi.addSection(t('spotlight.new_section'))
      .then(({ data: s }) => setRenaming({ id: s.id, name: s.name }), fail);
  };
  const saveName = () => {
    const r = renaming;
    setRenaming(null);
    if (r?.name?.trim()) spotlightApi.renameSection(r.id, r.name.trim()).catch(fail);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="spotlight-page">
      <div className="flex items-start justify-between gap-3 max-md:hidden">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />{t('spotlight.title')}
            <span className="text-sm font-normal text-muted-foreground tabular-nums">
              {data?.limit ? `${count}/${data.limit}` : count}
            </span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t('spotlight.hint')}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => setRulesOpen(true)} data-testid="spotlight-rules">
            <SlidersHorizontal className="w-4 h-4 mr-1.5" />{t('spotlight.rules')}
          </Button>
          <Button variant="outline" size="sm" onClick={addSection} data-testid="spotlight-add-section">
            <Plus className="w-4 h-4 mr-1.5" />{t('spotlight.add_section')}
          </Button>
        </div>
      </div>
      <div className="md:hidden flex items-center gap-2">
        <p className="text-sm text-muted-foreground flex-1">{t('spotlight.hint')}</p>
        <Button variant="outline" size="sm" onClick={() => setRulesOpen(true)} aria-label={t('spotlight.rules')}>
          <SlidersHorizontal className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={addSection} aria-label={t('spotlight.add_section')}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {!(data?.items || []).length && (
        <div className="rounded-xl border border-dashed border-border px-6 py-8 text-center" data-testid="spotlight-empty">
          <Sparkles className="w-6 h-6 mx-auto text-muted-foreground/60 mb-2" />
          <p className="text-sm text-foreground">{t('spotlight.empty_title')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('spotlight.empty')}</p>
        </div>
      )}

      {sections.map((section, index) => {
        const tasks = bySection[section.id] || [];
        return (
          <section key={section.id} data-testid="spotlight-section" data-section-id={section.id}
            // A task dragged onto the heading goes to the end of the section.
            onDragOver={(e) => { if (e.target.closest('[data-section-heading]')) e.preventDefault(); }}
            onDrop={(e) => {
              if (!e.target.closest('[data-section-heading]')) return;
              const id = e.dataTransfer.getData('text/plain');
              if (id) reorder(section.id, [...tasks.map((x) => x.id).filter((x) => x !== id), id]);
            }}>
            <div className="flex items-center gap-2 mb-2" data-section-heading>
              {renaming?.id === section.id ? (
                <input ref={renameInput} autoFocus value={renaming.name} maxLength={60}
                  onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                  onBlur={saveName}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setRenaming(null); }}
                  className="text-sm font-semibold bg-transparent border-b border-amber-500/60 outline-none text-foreground" />
              ) : (
                <h2 className="text-sm font-semibold text-foreground">{section.name}</h2>
              )}
              <span className="text-xs text-muted-foreground tabular-nums">{tasks.length}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
                    aria-label={t('common.more', 'More')}>
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48"
                  onCloseAutoFocus={(e) => { if (renamingId === section.id) e.preventDefault(); }}>
                  <DropdownMenuItem onSelect={() => setRenaming({ id: section.id, name: section.name })}>
                    <Pencil className="w-4 h-4 mr-2" />{t('common.rename')}
                  </DropdownMenuItem>
                  {index > 0 && (
                    <DropdownMenuItem onSelect={() => moveSection(index, -1)}><ArrowUp className="w-4 h-4 mr-2" />{t('spotlight.move_up')}</DropdownMenuItem>
                  )}
                  {index < sections.length - 1 && (
                    <DropdownMenuItem onSelect={() => moveSection(index, 1)}><ArrowDown className="w-4 h-4 mr-2" />{t('spotlight.move_down')}</DropdownMenuItem>
                  )}
                  {sections.length > 1 && (
                    <DropdownMenuItem onSelect={() => setRemoving(section)} className="text-red-500">
                      <Trash2 className="w-4 h-4 mr-2" />{t('spotlight.delete_section')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <TasksTable
              tasks={tasks}
              projects={projects}
              onUpdate={refresh}
              onOptimisticUpdate={patchTask}
              showProjectColumn
              showSearch={false}
              showFilters={false}
              showGrouping={false}
              showAddButton={false}
              emptyInlineAdd
              defaultGroupBy="none"
              defaultSortColumn="manual"
              tableId={`spotlight-${section.id}`}
              onManualOrder={(ids) => reorder(section.id, ids)}
              onInlineCreated={(task) => spotlightApi.put(task.id, section.id).catch(fail)}
            />
          </section>
        );
      })}

      <ConfirmModal
        isOpen={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          const s = removing;
          setRemoving(null);
          spotlightApi.deleteSection(s.id).catch(fail);
        }}
        title={t('spotlight.delete_section')}
        message={t('spotlight.delete_section_confirm')}
        confirmLabel={t('common.delete')}
        danger
      />
      <SpotlightRules open={rulesOpen} onClose={() => setRulesOpen(false)} sections={sections}
        rules={data?.rules || []} limit={data?.limit || null} projects={projects} />
    </div>
  );
};

export default SpotlightPage;
